import React, { FC, useEffect, useState } from 'react';
import { CalendarIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import CommentListing from 'components/CommentListing/CommentListing';
import Avatar from 'shared/Avatar/Avatar';
import LikeSaveButtons from 'components/LikeSaveButtons';
import { useNavigate, useParams } from 'react-router-dom';
import ButtonSecondary from 'shared/Button/ButtonSecondary';
import ButtonPrimary from 'shared/Button/ButtonPrimary';
import useService from '../../../@core/hooks/useService';
import { RotatingTriangles } from 'react-loader-spinner';
import { useGalleryImages } from '../../../@shared/hooks/useGalleryImages';
import { useReservationStore } from '../../../stores/Reservation.store';
import { useOrders } from '../../../hooks/collections/useOrdersV2';
import { useAlert } from '../../../@shared/hooks/useAlert';
import { useUserSessionStore } from '../../../stores/UserSession.store';
import { useLoader } from '../../../@shared/hooks/useLoader';
import QuillRenderer from '../../../@core/components/QuillRenderer';
import { UserTypeEnum } from '../../../@core/enums/UserType.enum';
import { ReviewStatusEnum } from '../../../enums/ReviewStatus.enum';
import { firebase } from '../../../firebase/config';
import { uniqBy } from 'lodash';
import schoonLogo from 'images/schoon_transparent.svg';
import ReservationWeekView from './ReservationWeekView';
import { useReservations } from '../../../hooks/collections/useReservations';
import moment from 'moment/moment';
import { OrderStatusEnum } from '../../../enums/OrderStatus.enum';
import { IReservation } from '../../../@core/interfaces/IReservation';

const ListingCarDetail: FC = () => {
  const { id } = useParams();
  const { service, isLoading } = useService(id as string);
  const router = useNavigate();
  const gallery = useGalleryImages();
  const {
    setServiceId,
    reset,
    isChoosingReservation,
    setIsChoosingReservation,
    dates,
    setDates
  } = useReservationStore();
  const user = useUserSessionStore((state) => state.user);
  const { createOrder } = useOrders({ loadData: false });
  const [hasMoreReviews, setHasMoreReviews] = useState(true);
  const alert = useAlert();
  const loader = useLoader();
  const [reservations, setReservations] = useState([]);
  const serviceId = useParams().id as string;
  const { reservations: reservationsSold, getReservations } = useReservations({
    loadData: true,
    queries: [
      { type: 'where', field: 'service.id', operator: '==', value: serviceId },
      { type: 'where', field: 'createdAt', operator: '>=', value: moment().startOf('day').valueOf() },
      {
        type: 'where',
        field: 'paymentStatus',
        operator: 'in',
        value: [OrderStatusEnum.PAID, OrderStatusEnum.GENERATED]
      }
    ]
  });

  const handleCreateOrder = async () => {
    if (!user) return alert.error('Please create an account or log in to continue.');
    if (user.role !== UserTypeEnum.USER) return alert.warning('Whoops', 'Please switch to a personal account to proceed.');
    if (!dates || dates.length === 0) return alert.toastWarning('Please select at least one date and time to proceed.');

    try {
      loader.start();
      const newOrder = await createOrder(dates, service);
      const checkoutFunction = firebase.functions().httpsCallable(`api/order/create-expiration-order-task/${newOrder.id}`);
      await checkoutFunction()
      router(`/checkout?orderId=${newOrder.id}`);
    } catch (e) {
      alert.error('Error', e.message);
      console.log(e);
    } finally {
      loader.stop();
    }
  };

  const handleOpenModalImageGallery = () => {
    if (!service?.images?.length) return;
    gallery.show(service?.images);
  };

  const handleLoadMoreReviews = async () => {
    if (!reservations.length) return;

    const limit = 5;
    try {
      const lastReservation = reservations[reservations.length - 1];
      const lastRating = lastReservation.rating;

      const newReservations = await getReservations([
        {
          type: 'where',
          field: 'rating',
          operator: '>',
          value: 0
        },
        {
          type: 'where',
          field: 'reviewStatus',
          operator: '==',
          value: ReviewStatusEnum.ACCEPTED
        },
        {
          type: 'where',
          field: 'service.id',
          operator: '==',
          value: id
        },
        {
          type: 'where',
          field: 'rating',
          operator: '<=',
          value: lastRating
        },
        {
          type: 'orderBy',
          field: 'rating',
          direction: 'desc'
        },
        {
          type: 'orderBy',
          field: 'createdAt',
          direction: 'desc'
        },
        {
          type: 'limit',
          limit: limit
        }
      ]);

      if (newReservations.length < limit) setHasMoreReviews(false);

      if (newReservations.length > 0) {
        const uniqueOrders = uniqBy([...reservations, ...newReservations], 'id');
        setReservations([...uniqueOrders]);
        return;
      }

      setHasMoreReviews(false);
    } catch (error) {
      console.error('Error loading more reviews:', error);
      setHasMoreReviews(false);
    }
  };

  useEffect(() => {
    reset();
    setServiceId(id as string);
  }, [id, service]);

  useEffect(() => {
    const initialLimit = 5;
    getReservations([
      {
        type: 'where',
        field: 'rating',
        operator: '>',
        value: 0
      },
      {
        type: 'where',
        field: 'reviewStatus',
        operator: '==',
        value: ReviewStatusEnum.ACCEPTED
      },
      {
        type: 'where',
        field: 'service.id',
        operator: '==',
        value: id
      },
      {
        type: 'orderBy',
        field: 'rating',
        direction: 'desc'
      },
      {
        type: 'orderBy',
        field: 'createdAt',
        direction: 'desc'
      },
      {
        type: 'limit',
        limit: initialLimit
      }
    ]).then((r: IReservation[]) => {
      setReservations(r);
      if (r.length < initialLimit) setHasMoreReviews(false);
    });
  }, []);

  const renderSection1 = () => {
    return (
      <div className="listingSection__wrap !space-y-6">
        <div className={'flex items-center justify-between'}>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold">
            {service?.title || ''}
          </h2>
          <div className="flex justify-end items-center">
            <LikeSaveButtons/>
          </div>
        </div>


        {/* 3 */}
        <div className="flex items-center space-x-4">
          {/*<StartRating/>*/}
          {/*<span>·</span>*/}
          <span>
            <i className="las la-map-marker-alt"></i>
            <span className="ml-1">{service.details.location.address}</span>
          </span>
        </div>

        {/* 4 */}
        <div className="flex items-center">
          <Avatar
            imgUrl={service.company.imageUrl}
            hasChecked
            sizeClass="h-10 w-10"
            radius="rounded-full"
          />
          <span className="ml-2.5 text-neutral-500 dark:text-neutral-400">
            Owner{' '}
            <span className="text-neutral-900 dark:text-neutral-200 font-medium">
              {service.company.name}
            </span>
          </span>
        </div>

        {/* 5 */}
        {/*<div className="w-full border-b border-neutral-100 dark:border-neutral-700"/>*/}

        {/* 6 */}
        {/*<div className="flex items-center justify-between xl:justify-start space-x-8 xl:space-x-12 text-sm text-neutral-700 dark:text-neutral-300">*/}
        {/*  <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 text-center sm:text-left sm:space-x-3 ">*/}
        {/*    <i className="las la-user-friends text-2xl"></i>*/}
        {/*    <span className="">4 seats</span>*/}
        {/*  </div>*/}
        {/*  <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 text-center sm:text-left sm:space-x-3 ">*/}
        {/*    <i className="las la-dharmachakra text-2xl"></i>*/}
        {/*    <span className=""> Auto gearbox</span>*/}
        {/*  </div>*/}
        {/*  <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 text-center sm:text-left sm:space-x-3 ">*/}
        {/*    <i className="las la-suitcase text-2xl"></i>*/}
        {/*    <span className=""> 2 bags</span>*/}
        {/*  </div>*/}
        {/*</div>*/}
      </div>
    );
  };

  const Description = () => {
    return (
      <div className="listingSection__wrap">
        <h2 className="text-2xl font-semibold">Description</h2>
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>
        {service?.description && (
          <QuillRenderer
            content={service.description}
            className="text-neutral-6000 dark:text-neutral-300"
          />
        )}
      </div>
    );
  };

  const BussinesHoursSection = () => {
    const WEEK_DAYS = [
      'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
    ];

    return (
      <div className="listingSection__wrap">
        <div>
          <h2 className="text-2xl font-semibold">Availability</h2>
          <span className="block mt-2 text-neutral-500 dark:text-neutral-400">
            We're available during the following hours:
          </span>
        </div>
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>
        {/* 6 */}
        <div className="mt-6">
          <div className="divide-y divide-neutral-200 dark:divide-neutral-700 rounded-md border border-neutral-200 dark:border-neutral-700 overflow-hidden">
            {WEEK_DAYS.map((day, index) => {
              const hours = service.details.location.openHours[index];
              return (
                <div
                  key={index}
                  className="flex items-center justify-between px-4 py-2 bg-white dark:bg-neutral-800"
                >
                  <span className="font-medium text-neutral-700 dark:text-neutral-200">
                    {day}
                  </span>
                  <span className="text-sm text-neutral-500 dark:text-neutral-400">
                    {hours && hours.length ? hours.join(', ') : 'Closed'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const CommentsReviews = () => {
    const serviceReviews = reservations
      ? reservations
        .map((reservation: IReservation) => ({
          name: reservation.createdBy?.name || 'Anonymous',
          avatar: reservation.createdBy?.imageUrl || '',
          date: new Date(reservation.createdAt).toLocaleDateString(),
          comment: reservation.comment || '',
          starPoint: reservation.rating || 0
        }))
      : [];

    return (
      <div className="listingSection__wrap">
        <h2 className="text-2xl font-semibold">Reviews</h2>
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>

        <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
          {serviceReviews.length > 0 ? (
            <>
              {serviceReviews.map((review, index) => (
                <CommentListing key={index} className="py-8" data={review}/>
              ))}

              {hasMoreReviews && (
                <div className="flex justify-center mt-8">
                  <ButtonSecondary onClick={handleLoadMoreReviews}>
                    Load more reviews
                  </ButtonSecondary>
                </div>
              )}
            </>
          ) : (
            <div className="py-8 text-center text-neutral-500">
              No reviews yet for this service.
            </div>
          )}
        </div>
      </div>
    );
  };

  const LocationSection = () => {
    return (
      <div className="listingSection__wrap">
        {/* HEADING */}
        <h2 className="text-2xl font-semibold">Location</h2>
        <span className="block mt-2 text-neutral-500 dark:text-neutral-400">
          {service?.details?.location?.address}
        </span>

        <div className="w-14 mt-6 border-b border-neutral-200 dark:border-neutral-700"/>

        {/* MAP */}
        <div className="mt-6 aspect-w-5 aspect-h-5 sm:aspect-h-3 ring-1 ring-black/10 rounded-xl z-0">
          <div className="rounded-xl overflow-hidden z-0">
            <iframe
              title="Location Map"
              width="100%"
              height="100%"
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
              src={`https://www.google.com/maps?q=${service.details.location.coordinates.lat},${service.details.location.coordinates.lng}&output=embed`}
            ></iframe>
          </div>
        </div>
      </div>
    );
  };

  const renderSection8 = () => {
    return (
      <div className="listingSection__wrap">
        {/* HEADING */}
        <h2 className="text-2xl font-semibold">Things to know</h2>
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"/>

        {/* CONTENT */}
        <div>
          <h4 className="text-lg font-semibold">Cancellation policy</h4>
          <span className="block mt-3 text-neutral-500 dark:text-neutral-400">
            Lock in this fantastic price today, cancel free of charge anytime.
            Reserve now and pay at pick-up.
          </span>
        </div>
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"/>

        {/* CONTENT */}
        <div>
          <h4 className="text-lg font-semibold">Special Note</h4>
          <span className="block mt-3 text-neutral-500 dark:text-neutral-400">
            We asked ourselves, “How can we make the dash not only look better,
            but also give the driver a better look outside?” The unexpected
            answer is having no hood above the available 10.25-inch digital
            instrument cluster...
          </span>
        </div>
      </div>
    );
  };

  const renderSidebarPrice = () => {
    return (
      <div className="listingSectionSidebar__wrap shadow-xl">
        <div className="flex items-center justify-between">
          <span className="text-3xl font-semibold">
            ${service?.price || 0}
            <span className="ml-1 text-base font-normal text-neutral-500 dark:text-neutral-400">
              /service
            </span>
          </span>

          <div>
            <span className={'opacity-50'}>Duration:</span> {service.details.duration > 1 ? `${service.details.duration} hours` : '1 hour'}
          </div>
          {/*<StartRating/>*/}
        </div>

        {/* FORM */}
        <div className="border border-neutral-200 dark:border-neutral-700 rounded-2xl">
          {service && (
            <div
              className={'flex items-center p-4 gap-3 cursor-pointer'}
              onClick={() => {
                setServiceId(service?.id);
                setIsChoosingReservation(true);
              }}
            >
              <div className="text-neutral-300 dark:text-neutral-400">
                <CalendarIcon className="w-5 h-5 lg:w-7 lg:h-7"/>
              </div>
              <div className="flex-grow text-left">
                {dates && dates.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {dates.map((ts) => {
                      const d = new Date(ts);
                      const label = `${d.toLocaleDateString('en-US', {
                        month: 'short',
                        day: '2-digit'
                      })} - ${d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
                      return (
                        <span
                          key={ts}
                          className="inline-flex items-center gap-2 bg-neutral-100 dark:bg-neutral-800 text-neutral-800 dark:text-neutral-200 rounded-full px-3 py-1 text-sm"
                        >
                          {label}
                          <button
                            className="ml-1 w-5 h-5 flex items-center justify-center rounded-full bg-neutral-300 dark:bg-neutral-700 text-neutral-800 dark:text-neutral-100"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDates(dates.filter((dts) => dts !== ts));
                            }}
                            aria-label="Remove selected time"
                            title="Remove"
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                  </div>
                ) : (
                  <span className="block xl:text-lg font-semibold">Select date & time</span>
                )}
                <span className="block text-xs text-neutral-500 dark:text-neutral-400 mt-3">Click to choose a date</span>
              </div>
            </div>
          )}
        </div>

        <ButtonPrimary
          disabled={!service.available}
          onClick={handleCreateOrder}
        >Reserve</ButtonPrimary>
      </div>
    );
  };

  return (
    <>
      {isLoading ? (<div className={'flex items-center justify-center h-screen'}>
        <RotatingTriangles
          visible={true}
          height="80"
          width="80"
          ariaLabel="rotating-triangles-loading"
        />
      </div>) : <div className={` nc-ListingCarDetailPage `}>
        {isChoosingReservation && service && (
          <ReservationWeekView service={service} reservations={reservationsSold}/>
        )}
        {/* SINGLE HEADER */}
        <header className="rounded-md sm:rounded-xl">
          <div className="relative grid grid-cols-4 gap-1 sm:gap-2 h-[600px]">
            <div
              onClick={handleOpenModalImageGallery}
              className="col-span-2 row-span-2 relative rounded-md sm:rounded-xl overflow-hidden cursor-pointer"
            >
              <img
                src={service?.images?.length ? service.images[0] : schoonLogo}
                alt="0"
                className="absolute inset-0 object-cover rounded-md sm:rounded-xl w-full h-full"
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 50vw"
              />
              <div className="absolute inset-0 bg-neutral-900 bg-opacity-20 opacity-0 hover:opacity-100 transition-opacity"></div>
            </div>

            {service && service?.images?.length > 1 && (
              <div
                onClick={handleOpenModalImageGallery}
                className="col-span-1 row-span-2 relative rounded-md sm:rounded-xl overflow-hidden cursor-pointer"
              >
                <img
                  className="absolute inset-0 object-cover rounded-md sm:rounded-xl w-full h-full"
                  src={schoonLogo}
                  alt="1"
                  sizes="400px"
                />
                <div className="absolute inset-0 bg-neutral-900 bg-opacity-20 opacity-0 hover:opacity-100 transition-opacity"></div>
              </div>
            )}

            {service?.images?.length >= 4 && service?.images.filter((_, i) => i >= 2 && i < 4).map((item, index) => (
              <div
                onClick={handleOpenModalImageGallery}
                key={index}
                className={`relative rounded-md sm:rounded-xl overflow-hidden ${
                  index >= 2 ? 'block' : ''
                }`}
              >
                <div className="aspect-w-4 aspect-h-3">
                  <img
                    className="absolute inset-0 object-cover w-full h-full rounded-md sm:rounded-xl "
                    src={item || ''}
                    alt="photos"
                    sizes="400px"
                  />
                </div>

                {/* OVERLAY */}
                <div
                  className="absolute inset-0 bg-neutral-900 bg-opacity-20 opacity-0 hover:opacity-100 transition-opacity cursor-pointer"
                  onClick={handleOpenModalImageGallery}
                />
              </div>
            ))}

            <div
              className="absolute hidden md:flex md:items-center md:justify-center left-3 bottom-3 px-4 py-2 rounded-xl bg-neutral-100 text-neutral-500 cursor-pointer hover:bg-neutral-200 z-10"
              onClick={handleOpenModalImageGallery}
            >
              <Squares2X2Icon className="h-5 w-5"/>

              <span className="ml-2 text-neutral-800 text-sm font-medium">
              Show all photos
            </span>
            </div>
          </div>
        </header>

        {/* MAIn */}
        <main className=" relative z-10 mt-11 flex flex-col lg:flex-row ">
          {/* CONTENT */}
          <div className="w-full lg:w-3/5 xl:w-2/3 space-y-8 lg:pr-10 lg:space-y-10">
            {renderSection1()}
            {Description()}
            {/*{IncludesSection()}*/}
            {BussinesHoursSection()}
            {LocationSection()}
            {renderSection8()}
            {CommentsReviews()}
          </div>

          {/* SIDEBAR */}
          <div className="block flex-grow mt-14 lg:mt-0">
            <div className="block sticky top-28">
              {renderSidebarPrice()}
            </div>
          </div>
        </main>
      </div>}
    </>
  );
};

export default function ListingCarDetailPage() {

  return (
    <div className="ListingDetailPage mb-10">
      <div className="container ListingDetailPage__content">
        <ListingCarDetail/>
      </div>

      {/* STICKY FOOTER MOBILE */}
      {/*<MobileFooterSticky/>*/}
    </div>
  );
}
